import React, { useRef, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { EmailInput } from "../../components/inputs/email-input.tsx";
import { PasswordInput } from "../../components/inputs/password-input.tsx";
import { Button } from "../../components/buttons/button.tsx";
import { Form, FormControl, FormField, FormItem } from "../../components/base/form";
import { Alert } from "../../components/alerts/alert.tsx";

import { FontStyles } from "../../utils/font-styles.ts";
import { TrackEvent } from "../../utils/analytics.ts";
import { InvisibleGoogleCaptcha, InvisibleGoogleCaptchaRef } from "../utils/captcha/invisible-google-captcha.tsx";
import { TABS } from "../user-login-sign-up-screen.tsx";
import { BadRequestModel } from "../../utils/bad-request-model.ts";

interface UserLoginFormProps {
  onAuthSuccess?: () => PromiseLike<void>;
}

const linkStyle = `underline w-full text-loch hover:text-loch-800 font-bold text-center ${FontStyles["body-md"]}`;

const UserSchema = z.object({
  email: z
    .string({ required_error: "Please enter an email address" })
    .email({ message: "Please enter a valid email address" }),
  password: z.string({ required_error: "Please enter a password" }).min(1, { message: "Please enter a password" }),
});

export type FormFields = z.infer<typeof UserSchema>;

export const LoginUserForm: React.FC<UserLoginFormProps> = ({ onAuthSuccess }) => {
  const captchaRef = useRef<InvisibleGoogleCaptchaRef>(null);
  const form = useForm<FormFields>({
    mode: "onTouched",
    resolver: zodResolver(UserSchema),
    reValidateMode: "onChange",
  });
  const [loading, setLoading] = useState(false);
  const [successful, setSuccessful] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    setLoading(true);
    const token = await captchaRef.current?.execute();

    const requestBody = {
      Username: data.email,
      Password: data.password,
      RememberMe: true,
      CaptchaToken: token,
    };
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    };

    try {
      const response = await fetch("/users/new-login", requestOptions);
      if (response.ok) {
        TrackEvent({ name: "login", method: "email" });
        await onAuthSuccess?.();
      } else {
        const responseBody = (await response.json()) as BadRequestModel;
        setSuccessful(false);
        setErrorMessage(responseBody.errors[""]?.[0] ?? "Unable to login, please try again");
        setLoading(false);
        captchaRef.current?.reset();
      }
    } catch {
      setSuccessful(false);
      setErrorMessage("Unable to login, please try again");
      setLoading(false);
      captchaRef.current?.reset();
    }
  };

  return (
    <Form {...form}>
      {!successful && (
        <div className="mb-5">
          <Alert
            color="red"
            message={errorMessage}
            renderLeadingIcon={() => <i className="fa-sharp fa-regular fa-triangle-exclamation" />}
            style="outlined"
            title="Error"
          />
        </div>
      )}

      <form className="flex flex-col gap-4.5 md:gap-7.5" onSubmit={(event) => void form.handleSubmit(onSubmit)(event)}>
        <div className="flex flex-col gap-4 md:gap-5">
          <FormField
            control={form.control}
            name={"email"}
            render={() => (
              <FormItem>
                <FormControl>
                  <Controller
                    name={"email"}
                    control={form.control}
                    defaultValue={""}
                    render={({ field: { onBlur, value, onChange } }) => (
                      <EmailInput
                        state={form.formState.errors.email ? "error" : "default"}
                        id="user-email"
                        placeholder="Enter your email address..."
                        label="Email address"
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        message={form.formState.errors.email?.message}
                      />
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={"password"}
            render={() => (
              <FormItem>
                <FormControl>
                  <Controller
                    name={"password"}
                    control={form.control}
                    defaultValue={""}
                    render={({ field: { onBlur, value, onChange } }) => (
                      <PasswordInput
                        state={form.formState.errors.password ? "error" : "default"}
                        id="user-password"
                        placeholder="Enter your password..."
                        label="Password"
                        value={value}
                        onBlur={onBlur}
                        onChange={onChange}
                        message={form.formState.errors.password?.message}
                      />
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <div className="m-auto">
            <a className={linkStyle} href="/users/forgot-password">
              Forgot your password?
            </a>
          </div>
        </div>
        <Button
          label={TABS["sign-in"].label}
          color="primary"
          loading={loading}
          loadingLabel="Signing in..."
          style="solid"
          disabled={loading || !form.getValues("email") || !form.getValues("password")}
          type="submit"
        />
        <InvisibleGoogleCaptcha ref={captchaRef} />
      </form>
    </Form>
  );
};
